import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { ContentSection } from './ContentSection'
import { RichContent } from './RichContent'
import { useTranslation } from 'react-i18next'
import { ContextBlock } from './ContextBlock'
import { BookmarkIconButton } from './BookmarkButton'
import { ContentZoomControls } from './ContentZoomControls'
import { EditThisPageButton } from './EditThisPageButton'
import { NotePanel } from './NotePanel'
import { useIsExpanded } from '@/hooks/useLayoutMode'
import { NotesFab } from './NotesFab'
import { useAppStore } from '@/store/appStore'
import { useContentScrollArea } from '@/hooks/useContentScrollArea'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import type { ApiPractice, ApiContentTag, ApiPracticeArea, ApiExternalFramework, ApiExternalFrameworkClause, GetContentBundleResponse } from '@/types/api'
import {ContentGroup} from "@/components/content/ContentGroup.tsx";
import { Breadcrumb, type BreadcrumbSegment } from '../layout/Breadcrumb'
import { GuardedLink } from './GuardedLink'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'
import { useFilterChips } from '@/hooks/useFilterChips'
import { FilterChips } from './FilterChips'

interface PracticeContentProps {
  practice: ApiPractice
  bundle: GetContentBundleResponse
  highlightTerm?: string
  scrollToCcId?: number
}

export function PracticeContent({ practice, bundle, highlightTerm, scrollToCcId }: PracticeContentProps) {
  const { t } = useTranslation()
  const navigate = useGuardedNavigate()
  const { selectedContentTagIds } = useAppStore()
  const [noteOpen, setNoteOpen] = useState(false)
  const chipsByPractice = useFilterChips(bundle)
  // Docked vs popped-out is a persisted user preference, not per-page state.
  // The floating note window needs a cursor to drag and a `resize` handle to size,
  // and it would cover the content it annotates on a phone. Below lg the panel is
  // always docked. The store value is read but never written: a desktop preference
  // must survive a visit on a small screen.
  const notePoppedPreference = useAppStore((s) => s.notesPoppedOut)
  const contentZoom = useAppStore((s) => s.contentZoom)
  const isExpandedLayout = useIsExpanded()
  const notePopped = notePoppedPreference && isExpandedLayout
  const setNotePopped = useAppStore((s) => s.setNotesPoppedOut)
  const [selectedClauseId, setSelectedClauseId] = useState<number | null>(null)
  // Kept separate so modal content doesn't clear while the exit animation plays
  const [displayedClauseId, setDisplayedClauseId] = useState<number | null>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  // Give the scroll area focus on navigation so arrow / page keys scroll the content
  useContentScrollArea(scrollAreaRef, practice.id)

  // Shared Set so each glossary term is only highlighted on its first occurrence
  // across all sections of this page (value, required, explanatory, activities, work products, context blocks).
  // Reset whenever the user navigates to a different practice.
  const seenGlossaryTermIds = useRef(new Set<number>())
  useEffect(() => {
    seenGlossaryTermIds.current = new Set()
  }, [practice.id])

  useEffect(() => {
    if (!scrollAreaRef.current) return
    if (highlightTerm && !scrollToCcId) {
      const firstMark = scrollAreaRef.current.querySelector('mark')
      firstMark?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else if (!highlightTerm && !scrollToCcId) {
      scrollAreaRef.current.scrollTop = 0
    }
  }, [highlightTerm, scrollToCcId])

  useEffect(() => {
    if (!scrollToCcId) return
    const tryScroll = (attemptsLeft: number) => {
      const el = document.getElementById(`cc-${scrollToCcId}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('context-block-highlight')
        const timer = setTimeout(() => el.classList.remove('context-block-highlight'), 3000)
        return () => clearTimeout(timer)
      } else if (attemptsLeft > 0) {
        setTimeout(() => tryScroll(attemptsLeft - 1), 150)
      }
    }
    tryScroll(10)
  }, [scrollToCcId])

  const tagMap = bundle.contentTags.reduce<Record<number, ApiContentTag>>((acc, t) => {
    acc[t.id] = t
    return acc
  }, {})

   // Find contextual content linked directly to this practice.
   // Items linked at the practice area level are intentionally excluded here —
   // those are shown on the practice area page instead.
   // Sort by the content tag's sortOrder to match the filter menu order.
   const contextualItems = bundle.contextualContent
     .filter((cc) => {
       const linkedToThis = cc.practiceId === practice.id
       if (!linkedToThis) return false
       // If -1 (all) is selected, don't filter (show all contexts). Otherwise, filter by selected tag IDs.
       if (selectedContentTagIds.length > 0 && !selectedContentTagIds.includes(-1) && !selectedContentTagIds.includes(cc.contentTagId)) return false
       return true
     })
     .sort((a, b) => {
       const tagA = tagMap[a.contentTagId]
       const tagB = tagMap[b.contentTagId]
       // Sort by content tag sortOrder (to match filter menu order)
       return (tagA?.sortOrder ?? 0) - (tagB?.sortOrder ?? 0)
     })

  // Build practice area map for looking up related PAs
  const practiceAreaMap = bundle.practiceAreas.reduce<Record<number, ApiPracticeArea>>((acc, pa) => {
    acc[pa.id] = pa
    return acc
  }, {})

  // Build breadcrumbs
  const practiceArea = practiceAreaMap[practice.practiceAreaId]
  const breadcrumbSegments: BreadcrumbSegment[] = []
  if (practiceArea) {
    breadcrumbSegments.push({
      label: practiceArea.displayName,
      href: `/practice-area/${practiceArea.id}`
    })
  }
  breadcrumbSegments.push({ label: practice.displayName })

  // Collect related practice areas for this practice (only where practice is the owner)
  const relatedPracticeAreas = bundle.practiceRelatedPracticeAreas
    .filter((r) => r.practiceId === practice.id)
    .map((r) => practiceAreaMap[r.relatedPracticeAreaId])
    .filter((pa): pa is ApiPracticeArea => pa !== undefined)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  // Build crosswalk mapping groups for this practice, keyed by framework
  const frameworkMap = (bundle.externalFrameworks ?? []).reduce<Record<number, ApiExternalFramework>>((acc, fw) => {
    acc[fw.id] = fw; return acc
  }, {})
  const clauseMap = (bundle.externalFrameworkClauses ?? []).reduce<Record<number, ApiExternalFrameworkClause>>((acc, c) => {
    acc[c.id] = c; return acc
  }, {})
  type CrosswalkRow = { clauseId: number; clauseKey: string; topic: string; relationshipType: number; note?: string | null }
  const crosswalkByFramework = (bundle.practiceCrosswalkMappings ?? [])
    .filter((m) => m.practiceId === practice.id)
    .reduce<Record<number, { framework: ApiExternalFramework; rows: CrosswalkRow[] }>>((acc, m) => {
      const clause = clauseMap[m.externalFrameworkClauseId]
      const framework = frameworkMap[m.externalFrameworkId]
      if (!clause || !framework) return acc
      if (!acc[framework.id]) acc[framework.id] = { framework, rows: [] }
      acc[framework.id].rows.push({ clauseId: clause.id, clauseKey: clause.clauseKey, topic: clause.topic, relationshipType: m.relationshipType, note: m.note })
      return acc
    }, {})
  const crosswalkGroups = Object.values(crosswalkByFramework)
    .sort((a, b) => a.framework.name.localeCompare(b.framework.name))
    .map(g => ({ ...g, rows: g.rows.sort((a, b) => a.clauseKey.localeCompare(b.clauseKey, undefined, { numeric: true, sensitivity: 'base' })) }))

  const practiceMap = bundle.practices.reduce<Record<number, ApiPractice>>((acc, p) => {
    acc[p.id] = p; return acc
  }, {})
  const modalClause = displayedClauseId !== null ? clauseMap[displayedClauseId] : null
  const modalFramework = modalClause ? frameworkMap[modalClause.externalFrameworkId] : null
  const modalPractices = displayedClauseId !== null
    ? (bundle.practiceCrosswalkMappings ?? [])
        .filter(m => m.externalFrameworkClauseId === displayedClauseId)
        .map(m => {
          const p = practiceMap[m.practiceId]
          const pa = p ? practiceAreaMap[p.practiceAreaId] : undefined
          return { mapping: m, practice: p, practiceArea: pa }
        })
        .filter((r): r is typeof r & { practice: ApiPractice } => r.practice !== undefined)
        .sort((a, b) =>
          (a.practiceArea?.sortOrder ?? 0) - (b.practiceArea?.sortOrder ?? 0) ||
          a.practice.sortOrder - b.practice.sortOrder
        )
    : []

  return (
    <div className={`practice-content rich-tables content-with-note-panel${noteOpen && !notePopped ? ' note-panel-open' : ''}`}>
      {/* Header */}
      <div className="practice-header">
        <div className="practice-header-title">
          <h1 className="practice-name">{practice.displayName}</h1>
          <FilterChips chips={chipsByPractice?.get(practice.id)} tooltips className="filter-chips--header" />
        </div>
        <BookmarkIconButton contentType="Practice" entityId={practice.id} />
        <EditThisPageButton adminPath={`/admin/practices/${practice.id}`} />
      </div>

      {/* Scrollable content area */}
      <div className="content-scroll-frame">
        <ContentZoomControls />
        <div className="content-scroll-area content-scroll-area--zoomable" ref={scrollAreaRef} tabIndex={0} style={{ '--content-zoom': contentZoom } as CSSProperties}>
          <Breadcrumb segments={breadcrumbSegments} />
          <ContentGroup heading={t('content.requiredPracticeInformation')}>
            <ContentSection
                id={`p-${practice.id}-statement`}
                heading={t('content.practice.practiceStatement')}
            >
              <RichContent html={practice.statement ?? ''} original={practice.originalFields?.statement} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
            </ContentSection>
            <ContentSection
              id={`p-${practice.id}-value`}
              heading={t('content.practice.value')}
            >
              <RichContent html={practice.value ?? ''} original={practice.originalFields?.value} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
            </ContentSection>

            <ContentSection
              id={`p-${practice.id}-required`}
              heading={t('content.practice.additionalRequiredInformation')}
            >
              {practice.additionalRequiredInformation ? (
                <RichContent html={practice.additionalRequiredInformation} original={practice.originalFields?.additionalRequiredInformation} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
              ) : (
                <p className="empty-section">{t('content.emptyContent')}</p>
              )}
            </ContentSection>
          </ContentGroup>
          <ContentGroup heading={t('content.practice.explanatoryPracticeInformation')}>
            <ContentSection
              id={`p-${practice.id}-explanatory`}
              heading={t('content.practice.additionalExplanatoryInformation')}
              collapsible
            >
              {practice.additionalExplanatoryInformation ? (
                <RichContent html={practice.additionalExplanatoryInformation} original={practice.originalFields?.additionalExplanatoryInformation} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
              ) : (
                <p className="empty-section">{t('content.emptyContent')}</p>
              )}
            </ContentSection>
            <ContentSection
              id={`p-${practice.id}-activities`}
              heading={t('content.exampleActivities')}
              collapsible
            >
              {practice.exampleActivities ? (
                <RichContent html={practice.exampleActivities} original={practice.originalFields?.exampleActivities} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
              ) : (
                <p className="empty-section">{t('content.emptyContent')}</p>
              )}
            </ContentSection>

            <ContentSection
              id={`p-${practice.id}-workproducts`}
              heading={t('content.exampleWorkProducts')}
              collapsible
            >
              {practice.exampleWorkProducts ? (
                <RichContent html={practice.exampleWorkProducts} original={practice.originalFields?.exampleWorkProducts} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
              ) : (
                <p className="empty-section">{t('content.emptyContent')}</p>
              )}
            </ContentSection>

            {/* Related Practice Areas */}
            {relatedPracticeAreas.length > 0 && (
              <ContentSection
                id={`p-${practice.id}-related`}
                heading={t('content.relatedPracticeAreas')}
              >
                <div className="related-practice-areas-list">
                  {relatedPracticeAreas.map((rpa) => (
                    <div key={rpa.id} className="related-practice-area-item">
                      <a
                        href={`/practice-area/${rpa.id}`}
                        onClick={(e) => {
                          e.preventDefault()
                          navigate(`/practice-area/${rpa.id}`)
                        }}
                        className="practice-name-link"
                      >
                        <span className="related-practice-area-acronym">{rpa.acronym}</span>
                        {' – '}
                        <span className="related-practice-area-name">{rpa.displayName}</span>
                      </a>
                    </div>
                  ))}
                </div>
              </ContentSection>
            )}
          </ContentGroup>

          {/* External Framework Crosswalk Mappings */}
          {crosswalkGroups.length > 0 && (
            <ContentSection
              id={`p-${practice.id}-crosswalks`}
              heading={t('content.externalFrameworkMappings')}
              collapsible
            >
              <div className="crosswalk-groups">
                {crosswalkGroups.map(({ framework, rows }) => (
                  <div key={framework.id} className="crosswalk-framework-group">
                    <div className="crosswalk-framework-header">
                      <GuardedLink
                        to={`/framework/${framework.id}`}
                        className="crosswalk-framework-name crosswalk-framework-name-link"
                        title={t('content.frameworkViewAll', { name: framework.name })}
                      >
                        {framework.name}
                      </GuardedLink>
                      {framework.referenceUrl && (
                        <a
                          href={framework.referenceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="crosswalk-framework-link"
                          aria-label={`Open ${framework.name} standard (external)`}
                        >
                          <OpenInNewIcon fontSize="inherit" />
                        </a>
                      )}
                    </div>
                    <ul className="crosswalk-clause-list">
                      {rows.map((row) => {
                        const relKey = String(row.relationshipType) as '1'|'2'|'3'|'4'|'5'|'6'
                        const relName = t(`content.crosswalkRelationshipTypes.${relKey}.name`, { defaultValue: `Type ${row.relationshipType}` })
                        const relDesc = t(`content.crosswalkRelationshipTypes.${relKey}.description`, { defaultValue: '' })
                        return (
                          <li key={row.clauseKey} className="crosswalk-clause-item">
                            <Tooltip
                              title={
                                relDesc ? (
                                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{relName}</div>
                                    <div>{relDesc}</div>
                                  </div>
                                ) : relName
                              }
                              placement="top"
                              arrow
                              slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                            >
                              <span className={`crosswalk-relationship-badge crosswalk-relationship-badge--rel${relKey}`}>
                                {relName}
                                <HelpOutlineIcon className="crosswalk-rel-info-icon" fontSize="inherit" />
                              </span>
                            </Tooltip>
                            <button
                              type="button"
                              className="crosswalk-clause-key-btn"
                              onClick={() => { setDisplayedClauseId(row.clauseId); setSelectedClauseId(row.clauseId) }}
                            >
                              {row.clauseKey}
                            </button>
                            <span className="crosswalk-clause-topic">{row.topic}</span>
                            {row.note && (
                              <span className="crosswalk-note">{row.note}</span>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </ContentSection>
          )}

          {/* Context-Specific Content blocks */}
          {contextualItems.length > 0 && (
            <div className="context-blocks">
              <h2 className="context-blocks-heading">{t('content.contextSpecific')}</h2>
              {contextualItems.map((cc) => (
                <ContextBlock
                  key={cc.id}
                  contextualContent={cc}
                  tag={tagMap[cc.contentTagId]}
                  bundle={bundle}
                  enableGlossary
                  seenTermIds={seenGlossaryTermIds.current}
                  highlightTerm={highlightTerm}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Note flyout panel */}
      {noteOpen && (
        <NotePanel
          contentType="Practice"
          entityId={practice.id}
          popped={notePopped}
          onPoppedChange={setNotePopped}
          onClose={() => setNoteOpen(false)}
        />
      )}

      {/* Floating action button. While the note is open it is rendered by
          NotePanel instead, anchored to the panel's top edge; the popped-out
          window has its own controls and needs no button at all. */}
      {!noteOpen && (
        <NotesFab
          contentType="Practice"
          entityId={practice.id}
          noteOpen={false}
          onToggle={() => setNoteOpen(true)}
        />
      )}

      {/* Clause detail modal — inverted crosswalk view */}
      <Dialog
        open={selectedClauseId !== null}
        onClose={() => setSelectedClauseId(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="clause-modal-title">
            <code className="crosswalk-clause-key clause-modal-key">{modalClause?.clauseKey}</code>
            <span className="clause-modal-topic">{modalClause?.topic}</span>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <p className="clause-modal-heading">{t('content.practicesMappedToClause')}</p>
          {modalPractices.length === 0 ? (
            <p className="empty-section">{t('content.emptyContent')}</p>
          ) : (
            <ul className="crosswalk-clause-list">
              {modalPractices.map(({ mapping, practice: p }) => {
                const relKey = String(mapping.relationshipType) as '1'|'2'|'3'|'4'|'5'|'6'
                const relName = t(`content.crosswalkRelationshipTypes.${relKey}.name`, { defaultValue: `Type ${mapping.relationshipType}` })
                const relDesc = t(`content.crosswalkRelationshipTypes.${relKey}.description`, { defaultValue: '' })
                return (
                  <li key={mapping.id} className="crosswalk-clause-item">
                    <Tooltip
                      title={relDesc ? (
                        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>{relName}</div>
                          <div>{relDesc}</div>
                        </div>
                      ) : relName}
                      placement="top"
                      arrow
                      slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                    >
                      <span className={`crosswalk-relationship-badge crosswalk-relationship-badge--rel${relKey}`}>
                        {relName}
                        <HelpOutlineIcon className="crosswalk-rel-info-icon" fontSize="inherit" />
                      </span>
                    </Tooltip>
                    <GuardedLink
                      to={`/practice/${p.id}`}
                      className="clause-practice-link"
                      onNavigate={() => setSelectedClauseId(null)}
                    >
                      <span className="clause-practice-name">{p.displayName}</span>
                    </GuardedLink>
                    {mapping.note && <span className="crosswalk-note">{mapping.note}</span>}
                  </li>
                )
              })}
            </ul>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {modalFramework ? (
            <Button
              endIcon={<ChevronRightIcon />}
              onClick={() => {
                setSelectedClauseId(null)
                navigate(`/framework/${modalFramework.id}`)
              }}
            >
              {t('content.frameworkGoTo')}
            </Button>
          ) : (
            <span />
          )}
          <Button onClick={() => setSelectedClauseId(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
